import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Brain,
  MessageSquare,
  Cpu,
  Layers,
  Search,
  Network,
  Zap,
} from 'lucide-react';
import { HubShell } from '@/components/layout/HubShell';
import { ConversationalAI } from './ConversationalAI';
import { TabIntelligenceDashboard } from './TabIntelligenceDashboard';
import ContextualIntelligence from './ContextualIntelligence';
import SessionIntelligence from './SessionIntelligence';
import PatternDetectionViewer from './PatternDetectionViewer';
import SmartActionsDashboard from './SmartActionsDashboard';
import { RelationshipMapPage } from './RelationshipMapPage';
import { EntityExtractionPage } from './EntityExtractionPage';

/**
 * AI hub — every AI-driven surface under one tab strip.
 */
export function AIHubPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Legacy AI hub section bookmarks.
  useEffect(() => {
    const section = searchParams.get('section');
    if (!section) return;

    if (section === 'proactive') {
      const next = new URLSearchParams(searchParams);
      next.set('section', 'tab-intel');
      setSearchParams(next, { replace: true });
      return;
    }
    if (section === 'aggregator') {
      const next = new URLSearchParams(searchParams);
      next.set('section', 'contextual');
      setSearchParams(next, { replace: true });
      return;
    }
    if (section === 'predict') {
      navigate('/tabs?section=predict', { replace: true });
      return;
    }
    if (section === 'visual') {
      const next = new URLSearchParams(searchParams);
      next.set('section', 'patterns');
      setSearchParams(next, { replace: true });
      return;
    }
    if (section === 'workflows') {
      const next = new URLSearchParams(searchParams);
      next.set('section', 'smart-actions');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, navigate]);

  const tabs = [
    { id: 'chat', label: 'Chat', icon: MessageSquare, render: () => <ConversationalAI /> },
    { id: 'tab-intel', label: 'Tab Intel', icon: Cpu, render: () => <TabIntelligenceDashboard /> },
    { id: 'contextual', label: 'Contextual', icon: Layers, render: () => <ContextualIntelligence /> },
    { id: 'session', label: 'Session', icon: Brain, render: () => <SessionIntelligence /> },
    { id: 'patterns', label: 'Patterns', icon: Search, render: () => <PatternDetectionViewer /> },
    { id: 'relationships', label: 'Relationships', icon: Network, render: () => <RelationshipMapPage /> },
    { id: 'smart-actions', label: 'Smart Actions', icon: Zap, render: () => <SmartActionsDashboard /> },
    { id: 'entities', label: 'Entities', icon: Search, render: () => <EntityExtractionPage /> },
  ];

  return (
    <HubShell
      title="AI"
      description="Chat, analyse, predict, and orchestrate your tabs with AI."
      icon={Brain}
      tabs={tabs}
      defaultSection="chat"
      tabsClassName="gap-3"
    />
  );
}

export default AIHubPage;
